/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports
import { css, jsx } from '@emotion/react';
import type { IntlShape } from 'react-intl';

import type { BorderMarkAttributes } from '@atlaskit/adf-schema/border';
import { imageBorderMessages as messages } from '@atlaskit/editor-common/media/imageBorder';
import Popup from '@atlaskit/editor-common/Popup';
import { DropdownMenuSharedCssClassName } from '@atlaskit/editor-common/styles/dropdown-menu';
import type { Icon } from '@atlaskit/editor-common/types/floating-toolbar';
import ColorPalette from '@atlaskit/editor-common/ui-color/ColorPalette';
import borderColorPalette from '@atlaskit/editor-common/ui-color/ColorPalette/borderColorPalette';
import { borderPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color/ColorPalette/paletteMessagesTokenModeNames';
import { ArrowKeyNavigationProvider } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider';
import { ArrowKeyNavigationType } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider/types';
import DropdownMenu from '@atlaskit/editor-common/ui-menu/DropdownMenu';
import type { MenuItem } from '@atlaskit/editor-common/ui-menu/DropdownMenu/types';
import ToolbarButton from '@atlaskit/editor-common/ui-menu/ToolbarButton';
import { hexToEditorBorderPaletteColor } from '@atlaskit/editor-palette/border';
import BorderIcon from '@atlaskit/icon/core/border';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StrokeWeightLargeIcon from '@atlaskit/icon/core/stroke-weight-large';
import StrokeWeightMediumIcon from '@atlaskit/icon/core/stroke-weight-medium';
import StrokeWeightSmallIcon from '@atlaskit/icon/core/stroke-weight-small';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Text } from '@atlaskit/primitives/compiled/text';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import {
	buttonStyle,
	buttonWrapperStyle,
	contextualMenuArrow,
	contextualSubMenu,
	dropdownOptionButton,
	dropdownWrapper,
	itemSpacing,
	menuItemDimensions,
	toolbarButtonWrapper,
} from './styles';

export interface ImageBorderProps {
	borderMark?: BorderMarkAttributes;
	intl: IntlShape;
	setBorder: (attrs: Partial<BorderMarkAttributes>) => void;
	toggleBorder: () => void;
}

// New padding for border options drop down
const dropdownOptionButtonNew = css({
	background: 'transparent',
	borderWidth: token('border.width.selected'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: 0,
	'&:focus': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderColor: token('color.border.focused'),
	},
});

const ImageBorder = ({
	intl: { formatMessage },
	toggleBorder,
	borderMark,
	setBorder,
}: ImageBorderProps): jsx.JSX.Element => {
	const popupTarget = useRef<HTMLDivElement>(null);
	const dropDownColorOptionButton = useRef<HTMLButtonElement>(null);
	const dropDownSizeOptionButton = useRef<HTMLButtonElement>(null);
	const colorSubmenuRef = useRef<HTMLDivElement>(null);
	const sizeSubmenuRef = useRef<HTMLDivElement>(null);
	const openDropdownButtonRef = useRef<HTMLButtonElement>(null);
	const enabled = !!borderMark;
	const color = borderMark?.color;
	const size = borderMark?.size;
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenByKeyboard, setIsOpenedByKeyboard] = useState(false);
	const [isColorSubmenuOpen, setIsColorSubmenuOpen] = useState(false);
	const [isSizeSubmenuOpen, setIsSizeSubmenuOpen] = useState(false);

	const handleColorSubmenuEsc = useCallback(() => {
		setIsOpenedByKeyboard(false);
		setIsColorSubmenuOpen(false);
		dropDownColorOptionButton?.current?.focus();
	}, []);

	const handleSizeSubmenuEsc = useCallback(() => {
		setIsOpenedByKeyboard(false);
		setIsSizeSubmenuOpen(false);
		dropDownSizeOptionButton?.current?.focus();
	}, []);

	const handleSubMenuRef = (ref: HTMLDivElement | null) => {
		if (!ref) {
			return;
		}
		const rect = ref.getBoundingClientRect();
		if (rect.left + rect.width > window.innerWidth) {
			ref.style.left = `-${rect.width}px`;
		}
	};

	const handleTriggerByKeyboard = (
		event: React.KeyboardEvent,
		allowedKeys: string[],
		callback: () => void,
	) => {
		if (!allowedKeys.includes(event.key)) {
			return;
		}
		event.preventDefault();
		callback();
		setIsOpenedByKeyboard(true);
	};

	const handleTriggerToolbarByKeyboard = (event: React.KeyboardEvent, callback: () => void) => {
		handleTriggerByKeyboard(event, ['Enter', ' '], callback);
	};

	const handleTriggerSubmenuByKeyboard = (event: React.KeyboardEvent, callback: () => void) => {
		const keys = expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)
			? ['Enter', 'ArrowRight']
			: ['Enter', ' '];
		handleTriggerByKeyboard(event, keys, callback);
	};

	useEffect(() => {
		const focusFirstOption = (submenuRef: React.RefObject<HTMLDivElement>, isOpen: boolean) => {
			if (!isOpenByKeyboard) {
				return;
			}
			if (isOpen && submenuRef.current) {
				const firstOption = submenuRef.current.querySelector('button');

				if (!firstOption) {
					return;
				}

				firstOption.focus();

				const keyboardEvent = new KeyboardEvent('keydown', {
					key: 'ArrowDown',
					bubbles: true,
				});

				firstOption.dispatchEvent(keyboardEvent);
			}
		};
		focusFirstOption(colorSubmenuRef, isColorSubmenuOpen);
		focusFirstOption(sizeSubmenuRef, isSizeSubmenuOpen);
	}, [isColorSubmenuOpen, isSizeSubmenuOpen, isOpenByKeyboard]);

	const borderSizeOptions: { icon: Icon; name: string; value: number }[] = [
		{
			name: formatMessage(messages.borderSizeSubtle),
			value: 1,
			icon: StrokeWeightSmallIcon,
		},
		{
			name: formatMessage(messages.borderSizeMedium),
			value: 2,
			icon: StrokeWeightMediumIcon,
		},
		{
			name: formatMessage(messages.borderSizeBold),
			value: 3,
			icon: StrokeWeightLargeIcon,
		},
	];

	const items: MenuItem[] = [
		{
			content: (
				<div>
					<button
						ref={dropDownColorOptionButton}
						type="button"
						aria-label={formatMessage(messages.borderColorDropdownAriaLabel)}
						data-testid="image-border-dropdown-button-color"
						css={
							expValEquals(
								'platform_editor_fix_media_toolbar_border_dropdown',
								'isEnabled',
								true,
								false,
							)
								? dropdownOptionButtonNew
								: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
									dropdownOptionButton
						}
						aria-expanded={isColorSubmenuOpen}
						onKeyDown={(e) =>
							handleTriggerSubmenuByKeyboard(e, () => {
								if (expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
									setIsColorSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsSizeSubmenuOpen(false);
										}
										return next;
									});
								} else {
									setIsColorSubmenuOpen(!isColorSubmenuOpen);
								}
							})
						}
					>
						<Text>{formatMessage(messages.borderColor)}</Text>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
						<div css={contextualMenuArrow} />
					</button>
					<div
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={DropdownMenuSharedCssClassName.SUBMENU}
						ref={colorSubmenuRef}
					>
						{isColorSubmenuOpen && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
							<div css={contextualSubMenu(0)} ref={handleSubMenuRef}>
								<ArrowKeyNavigationProvider
									type={ArrowKeyNavigationType.MENU}
									// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
									handleClose={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleColorSubmenuEsc();
									}}
									disableCloseOnArrowClick={true}
								>
									<ColorPalette
										ariaLabel={
											fg('platform_editor_a11y_border_radiogroup_label')
												? formatMessage(messages.borderColorRadioGroupAriaLabel)
												: undefined
										}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										onClick={(color: string) => {
											setBorder({ color });
											setIsOpen(!isOpen);
										}}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										onKeyDown={(color, _, event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												setBorder({ color });
												setIsOpen(!isOpen);
												setIsColorSubmenuOpen(false);
												setIsSizeSubmenuOpen(false);
												openDropdownButtonRef.current?.focus();
											}
										}}
										selectedColor={color ?? null}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										paletteOptions={{
											palette: borderColorPalette,
											paletteColorTooltipMessages: borderPaletteTooltipMessages,
											hexToPaletteColor: hexToEditorBorderPaletteColor,
										}}
									/>
								</ArrowKeyNavigationProvider>
							</div>
						)}
					</div>
				</div>
			),
			'data-testid': 'dropdown-item__Color',
			key: 'dropdown-menu-image-border-color-button',
			value: { name: 'color' },
			'aria-label': '',
			wrapperTabIndex: null,
		},
		{
			content: (
				<div>
					<button
						type="button"
						aria-label={formatMessage(messages.borderSizeDropdownAriaLabel)}
						data-testid="image-border-dropdown-button-size"
						css={
							expValEquals(
								'platform_editor_fix_media_toolbar_border_dropdown',
								'isEnabled',
								true,
								false,
							)
								? dropdownOptionButtonNew
								: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
									dropdownOptionButton
						}
						aria-expanded={isSizeSubmenuOpen}
						ref={dropDownSizeOptionButton}
						onKeyDown={(e) =>
							handleTriggerSubmenuByKeyboard(e, () => {
								if (expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
									setIsSizeSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsColorSubmenuOpen(false);
										}
										return next;
									});
								} else {
									setIsSizeSubmenuOpen(!isSizeSubmenuOpen);
								}
							})
						}
					>
						<Text>{formatMessage(messages.borderSize)}</Text>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
						<div css={contextualMenuArrow} />
					</button>

					<div
						//eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={DropdownMenuSharedCssClassName.SUBMENU}
						ref={sizeSubmenuRef}
					>
						{isSizeSubmenuOpen && (
							<ArrowKeyNavigationProvider
								type={ArrowKeyNavigationType.MENU}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								handleClose={(e) => {
									e.preventDefault();
									handleSizeSubmenuEsc();
								}}
								disableCloseOnArrowClick={true}
							>
								<div
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
									css={contextualSubMenu(1)}
									ref={handleSubMenuRef}
									role={
										fg('platform_editor_a11y_border_radiogroup_label') ? 'radiogroup' : undefined
									}
									aria-label={
										fg('platform_editor_a11y_border_radiogroup_label')
											? formatMessage(messages.borderSizeRadioGroupAriaLabel)
											: undefined
									}
								>
									{borderSizeOptions.map(({ name, value, icon }, idx) => {
										// Ignored via go/ees005
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										const ButtonIcon = icon as React.ComponentClass<any>;
										return (
											// Ignored via go/ees005
											// eslint-disable-next-line react/no-array-index-key
											<Tooltip key={idx} content={name}>
												{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
												<span css={buttonWrapperStyle}>
													<button
														type="button"
														/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */
														css={buttonStyle(value === size)}
														aria-label={name}
														role="radio"
														aria-checked={value === size}
														onClick={() => {
															setBorder({ size: value });
															setIsOpen(false);
														}}
														onKeyDown={(event) => {
															if (event.key === 'Enter' || event.key === ' ') {
																setBorder({ size: value });
																setIsOpen(false);
																setIsColorSubmenuOpen(false);
																setIsSizeSubmenuOpen(false);
																openDropdownButtonRef.current?.focus();
															}
														}}
														onMouseDown={(e) => {
															e.preventDefault();
														}}
													>
														<ButtonIcon
															color={value === size ? token('color.icon.inverse') : 'currentColor'}
															spacing="spacious"
															label=""
														/>
													</button>
												</span>
											</Tooltip>
										);
									})}
								</div>
							</ArrowKeyNavigationProvider>
						)}
					</div>
				</div>
			),
			'data-testid': 'dropdown-item__Size',
			key: 'dropdown-menu-image-border-size-button',
			value: { name: 'size' },
			'aria-label': '',
			wrapperTabIndex: null,
		},
	];

	/**
	 * We want to change direction of our dropdowns a bit early,
	 * not exactly when it hits the boundary.
	 */
	const fitTolerance = 10;
	const fitWidth = menuItemDimensions.width;
	const fitHeight = items.length * (menuItemDimensions.height + itemSpacing);

	const isAnySubMenuOpen = isSizeSubmenuOpen || isColorSubmenuOpen;

	return (
		<div>
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				css={toolbarButtonWrapper({
					enabled,
					isOpen,
				})}
			>
				<ToolbarButton
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="image-border-toolbar-btn"
					selected={enabled}
					onClick={toggleBorder}
					spacing="compact"
					aria-label={
						enabled ? formatMessage(messages.removeBorder) : formatMessage(messages.addBorder)
					}
					iconBefore={<BorderIcon color="currentColor" label="" spacing="spacious" />}
					title={enabled ? formatMessage(messages.removeBorder) : formatMessage(messages.addBorder)}
				/>
				<div ref={popupTarget}>
					<ToolbarButton
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className="image-border-toolbar-dropdown"
						ref={openDropdownButtonRef}
						selected={enabled || isOpen}
						aria-expanded={isOpen}
						aria-label={formatMessage(messages.borderOptions)}
						title={formatMessage(messages.borderOptions)}
						spacing="compact"
						iconBefore={
							<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
						}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onClick={() => {
							setIsOpen(!isOpen);
							setIsOpenedByKeyboard(false);
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onKeyDown={(e) => handleTriggerToolbarByKeyboard(e, () => setIsOpen(!isOpen))}
					/>
				</div>
			</div>
			<Popup
				target={popupTarget.current ? popupTarget.current : undefined}
				fitWidth={fitWidth + fitTolerance}
				fitHeight={fitHeight + fitTolerance}
				forcePlacement={true}
				stick={true}
			>
				<div
					onMouseLeave={
						expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)
							? undefined
							: () => {
									setIsColorSubmenuOpen(false);
									setIsSizeSubmenuOpen(false);
								}
					}
					/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */
					css={dropdownWrapper}
				>
					<DropdownMenu
						//This needs be removed when the a11y is completely handled
						//Disabling key navigation now as it works only partially
						//Same with packages/editor/editor-plugin-table/src/plugins/table/ui/FloatingContextualMenu/ContextualMenu.tsx
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						arrowKeyNavigationProviderOptions={{
							type: ArrowKeyNavigationType.MENU,
							disableArrowKeyNavigation: isAnySubMenuOpen,
						}}
						allowEnterDefaultBehavior={isAnySubMenuOpen}
						handleEscapeKeydown={
							isAnySubMenuOpen
								? () => {
										return;
									}
								: () => {
										openDropdownButtonRef.current?.focus();
									}
						}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						items={[{ items }]}
						isOpen={isOpen}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						shouldFocusFirstItem={() => isOpenByKeyboard}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onOpenChange={() => {
							setIsOpen(false);
							setIsColorSubmenuOpen(false);
							setIsSizeSubmenuOpen(false);
							setIsOpenedByKeyboard(false);
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onItemActivated={({ item }) => {
							if (item.value.name === 'color') {
								if (expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
									setIsColorSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsSizeSubmenuOpen(false);
										}
										return next;
									});
								} else {
									setIsColorSubmenuOpen(!isColorSubmenuOpen);
								}
							}
							if (item.value.name === 'size') {
								if (expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
									setIsSizeSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsColorSubmenuOpen(false);
										}
										return next;
									});
								} else {
									setIsSizeSubmenuOpen(!isSizeSubmenuOpen);
								}
							}
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onMouseEnter={({ item }) => {
							if (!expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
								if (item.value.name === 'color') {
									setIsColorSubmenuOpen(true);
									setIsOpenedByKeyboard(false);
								}
								if (item.value.name === 'size') {
									setIsSizeSubmenuOpen(true);
									setIsOpenedByKeyboard(false);
								}
							}
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onMouseLeave={({ item }) => {
							if (!expValEquals('platform_editor_toolbar_submenu_open_click', 'isEnabled', true)) {
								if (item.value.name === 'color') {
									setIsColorSubmenuOpen(false);
								}
								if (item.value.name === 'size') {
									setIsSizeSubmenuOpen(false);
								}
							}
						}}
						fitWidth={fitWidth + fitTolerance}
						fitHeight={fitHeight + fitTolerance}
					/>
				</div>
			</Popup>
		</div>
	);
};

export default ImageBorder;
